import { z } from 'zod';
import {
  IdSchema,
  ImageRefSchema,
  LocationSchema,
  PriceSchema,
  SlugSchema,
  UrlSchema,
} from './primitives.js';
import { PaginatedResponseSchema, PaginationSchema } from './pagination.js';
import { LOCALES, DEFAULT_LOCALE } from './i18n/locales.js';

export const EventStatusSchema = z.enum([
  'upcoming',
  'on-sale',
  'sold-out',
  'past',
  'cancelled',
]);

/**
 * Event — la API y el admin sólo dependen de los campos requeridos
 * (id, slug, title, city, startAt). El resto son extensiones opcionales
 * que utiliza la web pública para renderizar el design system.
 */
export const EventSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  city: z.string(),
  startAt: z.string(),
  venue: z.string().optional(),
  imageUrl: z.string().optional(),
  minPrice: z.number().nonnegative().optional(),

  description: z.string().optional(),
  cover: ImageRefSchema.optional(),
  gallery: z.array(ImageRefSchema).optional(),
  artists: z.array(IdSchema).optional(),
  organizerId: IdSchema.optional(),
  location: LocationSchema.optional(),
  endAt: z.string().optional(),
  doorsAt: z.string().optional(),
  status: EventStatusSchema.optional(),
  genres: z.array(z.string()).optional(),
  price: PriceSchema.optional(),
  ticketUrl: UrlSchema.optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

/**
 * Query del listado público de eventos. Alineada con los query params
 * que acepta el endpoint B2C del Core (`POST /api/events/list`):
 * filtros por id de city/category/subCategory/artist/organization,
 * paginación nativa y orden con dos valores: 0 (defecto, fecha asc)
 * y 1 (precio asc).
 */
export const EventsQuerySchema = PaginationSchema.extend({
  city: z.string().optional(),
  categoryId: z.string().optional(),
  subCategoryId: z.string().optional(),
  artistId: z.string().optional(),
  organizationId: z.string().optional(),
  sortBy: z.coerce.number().int().min(0).max(1).optional(),
});

/**
 * Item proyectado al cliente. Subconjunto estable de `EventSchema`,
 * acotado a lo que la card del listado necesita pintar.
 */
export const EventPublicItemSchema = z.object({
  id: z.string(),
  slug: z.string().optional(),
  title: z.string(),
  imageUrl: z.string().optional(),
  // Variante 1:1 del Core, aparte de `imageUrl` (horizontal). La
  // rellenan `EventsService.getPublicBySlug` (bloque de evento del
  // blog, BFMWEB-178) y `search.logic.ts` (buscador, BFMWEB-215); el
  // resto de productores de `EventPublicItem` (listado) la dejan sin
  // definir.
  squareImageUrl: z.string().optional(),
  startAt: z.string().optional(),
  endAt: z.string().optional(),
  city: z.string().optional(),
  venue: z.string().optional(),
  minPrice: z.number().nonnegative().optional(),
  currency: z.string().optional(),
});

export const EventListPublicResponseSchema = PaginatedResponseSchema(
  EventPublicItemSchema,
);

/**
 * Respuesta del buscador público (`GET /v1/search?q=`). Devuelve eventos
 * y colecciones coincidentes. Las colecciones se indexan por título en el
 * locale activo (sin cascada: si el locale no tiene título, la colección no
 * sale). Solo página 1 incluye colecciones; page > 1 devuelve `collections: []`.
 */
// `pageSize` NO se acepta del cliente: lo fija el servidor (ver
// SEARCH_PAGE_SIZE en apps/api). Si fuera un parámetro, un atacante
// podría pedir páginas enormes contra el buscador en memoria.
export const EventSearchQuerySchema = z.object({
  q: z.string().min(1),
  page: z.coerce.number().int().positive().default(1),
  locale: z.enum(LOCALES).default(DEFAULT_LOCALE),
});

/** Hit de colección del buscador. `title` ya proyectado al locale
 *  (sin cascada: si el locale no tiene título, la colección no sale).
 *  Sin fechas: `dateFrom`/`dateTo` son agregados que se calculan en
 *  lectura contra el Core y no están disponibles en el índice. */
export const CollectionSearchItemSchema = z.object({
  slug: SlugSchema,
  title: z.string().min(1),
  eventCount: z.number().int().nonnegative(),
  thumbnailUrl: z.string().optional(),
});

export const EventSearchPublicResponseSchema = z.object({
  q: z.string(),
  items: z.array(EventPublicItemSchema),
  collections: z.array(CollectionSearchItemSchema),
});

export const EventCreateSchema = EventSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type EventStatus = z.infer<typeof EventStatusSchema>;
export type Event = z.infer<typeof EventSchema>;
export type EventsQuery = z.infer<typeof EventsQuerySchema>;
export type EventPublicItem = z.infer<typeof EventPublicItemSchema>;
export type EventListPublicResponse = z.infer<
  typeof EventListPublicResponseSchema
>;
export type EventSearchQuery = z.infer<typeof EventSearchQuerySchema>;
export type EventSearchPublicResponse = z.infer<
  typeof EventSearchPublicResponseSchema
>;
export type CollectionSearchItem = z.infer<
  typeof CollectionSearchItemSchema
>;
export type EventCreate = z.infer<typeof EventCreateSchema>;
